import {ApiClientError} from '@/api/client';
import {Link, useNavigate} from 'react-router-dom';
import {getBrowserTimezone} from '@/lib/timezone';
import {useCallback, useState} from 'react';
import {useCreateSite} from '@/hooks/useCreateSite';
import {useSlugCheck} from '@/hooks/useSlugCheck';

function slugify(text: string): string {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 63);
}

const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 48,
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 400,
    borderRadius: 8,
    letterSpacing: '-0.013em',
    border: '1px solid var(--tt-card-border)',
    color: 'var(--tt-text-primary)',
    background: 'var(--tt-card-bg)',
    outline: 'none',
    transition: 'border-color .15s linear, box-shadow .15s linear',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit'
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    margin: '0 0 .3em',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--tt-text-primary)'
};

function handleInputFocus(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'var(--tt-accent)';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
}

function handleInputBlur(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'var(--tt-card-border)';
    e.currentTarget.style.boxShadow = 'none';
}

export function CreateSitePage({modal = false}: {modal?: boolean}) {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
    const [error, setError] = useState('');

    const slugCheck = useSlugCheck(slug);
    const createSite = useCreateSite();

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        if (!slugManuallyEdited) {
            setSlug(slugify(value));
        }
    }, [slugManuallyEdited]);

    const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSlugManuallyEdited(true);
        setSlug(slugify(e.target.value));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!name.trim()) {
            setError('Please enter a newsletter title.');
            return;
        }
        if (!slug || slug.length < 2) {
            setError('Newsletter address must be at least 2 characters.');
            return;
        }
        if (slugCheck.data && !slugCheck.data.available) {
            setError('This newsletter address is not available. Try another one.');
            return;
        }
        try {
            const site = await createSite.mutateAsync({name: name.trim(), slug, timezone: getBrowserTimezone()});
            navigate(`/sites/${site.id}/provisioning`);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setError(err.message);
            } else {
                setError('Something went wrong. Please try again.');
            }
        }
    };

    const isSlugChecking = slug.length >= 2 && (slugCheck.isLoading || slugCheck.isFetching);
    const isSlugAvailable = slugCheck.data?.available === true && !isSlugChecking;
    const isSlugTaken = slugCheck.data?.available === false && !isSlugChecking;
    const canSubmit = name.trim().length > 0 && slug.length >= 2 && isSlugAvailable && !createSite.isLoading;

    return (
        <div className={modal ? 'studio-create-dialog-form' : 'studio-content-page'}>
            {!modal && <header className="studio-detail-heading">
                <Link className="studio-back-link" to="/dashboard">← Newsletters</Link>
                <h1 className="studio-page-title">Create a newsletter</h1>
                <p className="studio-page-description">Choose a name and address for your publication.</p>
            </header>}
            <div className={modal ? undefined : 'studio-form-panel'}>
                <form autoComplete="off" onSubmit={handleSubmit}>
                    <div style={{marginBottom: 20, position: 'relative'}}>
                        <label htmlFor="newsletter-title" style={labelStyle}>Newsletter title</label>
                        <input autoComplete="off"
                            className="studio-field-control"
                            id="newsletter-title"
                            placeholder="The Daily Awesome"
                            style={inputStyle}
                            type="text" value={name}
                            autoFocus
                            onBlur={handleInputBlur}
                            onChange={handleNameChange}
                            onFocus={handleInputFocus}
                        />
                    </div>

                    <div style={{marginBottom: 36, position: 'relative'}}>
                        <label htmlFor="newsletter-address" style={labelStyle}>Newsletter address</label>
                        <div style={{position: 'relative'}}>
                            <input autoComplete="off"
                                className="studio-field-control"
                                id="newsletter-address"
                                placeholder="the-daily-awesome"
                                style={{...inputStyle, paddingRight: 44, borderRadius: 8, background: 'var(--tt-input-bg)', border: '1px solid var(--tt-input-border)', fontSize: '13px', fontFamily: 'inherit'}}
                                type="text" value={slug}
                                onBlur={handleInputBlur}
                                onChange={handleSlugChange}
                                onFocus={handleInputFocus}
                            />
                            {slug.length >= 2 && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: 14,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {isSlugChecking && (
                                        <svg height="18" style={{animation: 'spin 1s linear infinite'}} viewBox="0 0 16 16" width="18">
                                            <circle cx="8" cy="8" fill="none" r="6" stroke="#CED4D9" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" strokeWidth="2"/>
                                        </svg>
                                    )}
                                    {isSlugAvailable && (
                                        <svg fill="none" height="20" viewBox="0 0 24 24" width="20">
                                            <path d="M20 6L9 17L4 12" stroke="var(--studio-accent)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"/>
                                        </svg>
                                    )}
                                    {isSlugTaken && (
                                        <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
                                            <path d="M18 6L6 18M6 6L18 18" stroke="#F50B23" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"/>
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                        {slug && (
                            <div
                                style={{
                                    position: 'absolute',
                                    right: 0,
                                    bottom: -22,
                                    margin: 0,
                                    color: isSlugTaken ? 'var(--tt-red)' : 'var(--tt-text-secondary)',
                                    fontSize: '1.35rem'
                                }}
                            >
                                {isSlugTaken
                                    ? (slugCheck.data?.error || 'This address is already taken')
                                    : `${slug}.tribetale.com`
                                }
                            </div>
                        )}
                    </div>

                    {error && (
                        <div
                            style={{
                                marginTop: 16,
                                color: 'var(--tt-red)',
                                fontSize: '13px',
                                lineHeight: 1.5,
                                textAlign: 'center'
                            }}
                        >
                            {error.toLowerCase().includes('upgrade') ? (
                                <>
                                    {error.split(/upgrade/i)[0]}
                                    <Link style={{color: 'var(--tt-accent)', fontWeight: 500, textDecoration: 'underline'}} to="/billing/upgrade">upgrade</Link>
                                    {error.split(/upgrade/i)[1]}
                                </>
                            ) : error}
                        </div>
                    )}

                    <button className="studio-control studio-control-primary"
                        disabled={!canSubmit}
                        style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', margin: '40px 0 0', cursor: canSubmit ? 'pointer' : 'default', fontFamily: 'inherit', letterSpacing: '-0.005em'}}
                        type="submit"
                        onMouseEnter={(e) => {
                            if (canSubmit) {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent-hover)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (canSubmit) {
                                e.currentTarget.style.backgroundColor = 'var(--tt-accent)';
                            }
                        }}
                    >
                        {createSite.isLoading ? (
                            <>
                                <svg height="16" style={{animation: 'spin 1s linear infinite'}} viewBox="0 0 16 16" width="16">
                                    <circle cx="8" cy="8" fill="none" r="6" stroke="currentColor" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" strokeWidth="2"/>
                                </svg>
                                Creating your newsletter...
                            </>
                        ) : (
                            <>Create newsletter</>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
